import { Component, EventEmitter, HostListener, Input, OnDestroy, Output } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { InboxPreviewClient, InboxPreviewState } from 'src/app/models/communication/inbox-preview';
import { DataService } from 'src/app/services/data.service';
import { SiteService } from 'src/app/services/site.service';

type DeviceFilter = 'all' | 'desktop' | 'phone';
type ModeFilter = 'all' | 'light' | 'dark';

/**
 * "Preview in Inboxes": screenshots of the saved campaign in real mail apps (Mailgun Inspect).
 * Opening the window reloads the latest run for free; only the Run button spends previews.
 * While a run is in progress the window polls until every mail app has finished.
 */
@Component({
	selector: 'app-inbox-preview',
	templateUrl: './inbox-preview.component.html',
	styleUrls: ['./inbox-preview.component.scss']
})
export class InboxPreviewComponent implements OnDestroy {

	@Input() campaignID: string;
	@Input() employers: Array<{ employerID: string, employerName: string }> = [];
	/** The editor has unsaved changes: previews use the saved version, so Run is held back. */
	@Input() unsavedChanges = false;

	private _visible = false;
	@Input() get visible() { return this._visible; }
	set visible(v: boolean) {
		const opening = v && !this._visible;
		this._visible = v;
		if (opening) this.load();
		if (!v) this.stopPolling();
	}
	@Output() visibleChange = new EventEmitter<boolean>();

	public state: InboxPreviewState = null;
	public loading = false;
	public running = false;
	public error: string = null;
	public employerID: string = null;

	public deviceFilter: DeviceFilter = 'all';
	public modeFilter: ModeFilter = 'all';
	public deviceOptions = [
		{ label: 'All', value: 'all' }, { label: 'Desktop', value: 'desktop' }, { label: 'Phone', value: 'phone' }
	];
	public modeOptions = [
		{ label: 'All', value: 'all' }, { label: 'Light', value: 'light' }, { label: 'Dark', value: 'dark' }
	];

	/** The screenshot shown full size, or null for the grid. */
	public selected: InboxPreviewClient = null;

	private poll: Subscription;

	constructor(private dataService: DataService, private siteService: SiteService, private confirmationService: ConfirmationService) { }

	ngOnDestroy() {
		this.stopPolling();
	}

	get run() {
		return this.state?.run;
	}

	get clients(): InboxPreviewClient[] {
		return (this.run?.clients ?? []).filter(c =>
			(this.deviceFilter === 'all' || (this.deviceFilter === 'phone') === this.isPhone(c)) &&
			(this.modeFilter === 'all' || (this.modeFilter === 'dark') === c.darkMode));
	}

	get doneCount() {
		return (this.run?.clients ?? []).filter(c => !this.isWorking(c)).length;
	}

	isPhone(c: InboxPreviewClient) {
		return c.category === 'Mobile';
	}

	isWorking(c: InboxPreviewClient) {
		return c.status === 'Pending' || c.status === 'Processing';
	}

	isFailed(c: InboxPreviewClient) {
		return !this.isWorking(c) && c.status !== 'Complete';
	}

	close() {
		this.selected = null;
		this.visible = false;
		this.visibleChange.emit(false);
	}

	load() {
		this.loading = true;
		this.error = null;
		this.selected = null;
		this.dataService.getInboxPreview(this.campaignID).subscribe({
			next: s => {
				this.loading = false;
				this.setState(s);
			},
			error: err => {
				this.loading = false;
				this.error = this.messageFrom(err, 'The previews could not be loaded.');
			}
		});
	}

	runPreview() {
		if (!this.employerID || this.running || this.unsavedChanges) return;
		// Re-running unchanged content as the same employer spends previews for identical pictures.
		if (this.run && !this.run.isOutdated && this.run.employerID === this.employerID) {
			this.confirmationService.confirm({
				header: 'Run again?',
				message: `These previews already match the saved email. Running again uses ${this.state.clientsPerRun} more previews.`,
				acceptLabel: 'Run again',
				rejectLabel: 'Cancel',
				accept: () => this.startRun()
			});
			return;
		}
		this.startRun();
	}

	private startRun() {
		this.running = true;
		this.error = null;
		this.selected = null;
		this.dataService.runInboxPreview(this.campaignID, this.employerID).subscribe({
			next: s => {
				this.running = false;
				this.setState(s);
			},
			error: err => {
				this.running = false;
				this.error = this.messageFrom(err, 'The preview could not be started.');
			}
		});
	}

	open(c: InboxPreviewClient) {
		if (c.screenshotUrl && c.status === 'Complete') this.selected = c;
	}

	step(delta: number) {
		const shown = this.clients.filter(c => c.screenshotUrl && c.status === 'Complete');
		const i = shown.indexOf(this.selected);
		if (i < 0 || shown.length === 0) return;
		this.selected = shown[(i + delta + shown.length) % shown.length];
	}

	@HostListener('document:keydown', ['$event'])
	onKey(e: KeyboardEvent) {
		if (!this.visible || !this.selected) return;
		if (e.key === 'ArrowRight') this.step(1);
		else if (e.key === 'ArrowLeft') this.step(-1);
		else if (e.key === 'Escape') { this.selected = null; e.stopPropagation(); }
	}

	private setState(s: InboxPreviewState) {
		// Keep the open screenshot open across polls (its object is replaced each time).
		const openID = this.selected?.clientID;
		this.state = s;
		this.selected = openID ? (s.run?.clients.find(c => c.clientID === openID) ?? null) : null;
		if (!this.employerID && s.run) this.employerID = s.run.employerID;
		if (s.run && !s.run.isFinished) this.startPolling();
		else this.stopPolling();
	}

	private startPolling() {
		if (this.poll) return;
		this.poll = timer(5000, 5000).pipe(switchMap(() => this.dataService.getInboxPreview(this.campaignID))).subscribe({
			next: s => this.setState(s),
			error: err => {
				this.stopPolling();
				this.error = this.messageFrom(err, 'The previews could not be refreshed.');
			}
		});
	}

	private stopPolling() {
		this.poll?.unsubscribe();
		this.poll = null;
	}

	/** 400s carry a plain message from the server; 500s are already reported by the error interceptor. */
	private messageFrom(err: any, fallback: string) {
		return err?.status === 400 ? (err.error?.message ?? fallback) : fallback;
	}
}
